import { useCallback, useEffect, useState } from "react";
import type { AuthUser, UserSettings } from "../types";

interface AuthError extends Error {
  status?: number;
}

async function postJson(url: string, body: unknown): Promise<Response> {
  return fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

async function readError(resp: Response): Promise<AuthError> {
  let message = `Erro ${resp.status}`;
  try {
    const j = (await resp.json()) as { error?: string };
    if (j.error) message = j.error;
  } catch {
    /* ignore */
  }
  const err: AuthError = new Error(message);
  err.status = resp.status;
  return err;
}

export function useAuth() {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const r = await fetch("/api/auth/me");
      if (r.ok) {
        const j = (await r.json()) as { user: AuthUser };
        setUser(j.user);
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  /**
   * Sessão expirada com a tela aberta: as chamadas passam a devolver 401 e a UI
   * fica ali, vazia e quebrada. Aqui a gente observa as respostas e, no primeiro
   * 401, limpa o usuário - o App troca para a tela de login sozinho.
   *
   * A URL NÃO é alterada (o `?s=` da conversa continua lá), então depois de
   * entrar de novo a pessoa cai exatamente na conversa onde parou.
   */
  useEffect(() => {
    const fetchOriginal = window.fetch;
    window.fetch = async (...args) => {
      const resposta = await fetchOriginal(...args);
      try {
        const url = new URL(
          typeof args[0] === "string" ? args[0] : (args[0] as Request).url,
          window.location.origin,
        );
        const ehApi = url.origin === window.location.origin && url.pathname.startsWith("/api/");
        // /api/auth/* responde 401 no login errado: ali o erro é do formulário
        const ehAuth = url.pathname.startsWith("/api/auth/");
        if (ehApi && !ehAuth && resposta.status === 401) {
          sessionStorage.setItem("ea_sessao_expirada", "1");
          setUser(null);
        }
      } catch {
        /* URL exótica: ignora */
      }
      return resposta;
    };
    return () => {
      window.fetch = fetchOriginal;
    };
  }, []);

  const login = useCallback(
    async (email: string, password: string): Promise<{ user: AuthUser } | { needsTwoFactor: true }> => {
      const r = await postJson("/api/auth/login", { email, password });
      if (!r.ok) throw await readError(r);
      const j = (await r.json()) as { user?: AuthUser; needsTwoFactor?: boolean };
      if (j.needsTwoFactor) return { needsTwoFactor: true };
      setUser(j.user ?? null);
      return { user: j.user as AuthUser };
    },
    [],
  );

  /** Segundo passo do login quando a conta tem 2FA: código do app autenticador ou um backup code. */
  const verifyTwoFactor = useCallback(async (opts: { token?: string; backupCode?: string }): Promise<AuthUser> => {
    const r = await postJson("/api/auth/2fa/verify-login", opts);
    if (!r.ok) throw await readError(r);
    const j = (await r.json()) as { user: AuthUser };
    setUser(j.user);
    return j.user;
  }, []);

  const register = useCallback(
    async (
      email: string,
      name: string,
      password: string,
    ): Promise<{ user: AuthUser; needsApproval: boolean }> => {
      const r = await postJson("/api/auth/register", { email, name, password });
      if (!r.ok) throw await readError(r);
      const j = (await r.json()) as { user: AuthUser; needsApproval: boolean };
      // a org é resolvida pelo domínio (ex.: selb.assistant... -> Selbetti); só
      // sysadmin (ADMIN_EMAILS) vem logado direto - o resto aguarda aprovação
      // do admin daquela org.
      if (!j.needsApproval) setUser(j.user);
      return j;
    },
    [],
  );

  const logout = useCallback(async () => {
    try {
      await postJson("/api/auth/logout", {});
    } catch {
      /* ignore */
    }
    // escopo de org do sysadmin não atravessa sessões (reseta ao relogar)
    try {
      localStorage.removeItem("ea_admin_org_scope");
    } catch {
      /* ignore */
    }
    setUser(null);
  }, []);

  /** Persiste preferências do usuário no servidor (otimista no cliente). */
  const updateSettings = useCallback(async (patch: Partial<UserSettings>) => {
    setUser((prev) => (prev ? { ...prev, settings: { ...prev.settings, ...patch } } : prev));
    try {
      const r = await fetch("/api/auth/settings", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      });
      if (r.ok) {
        const j = (await r.json()) as { user: AuthUser };
        setUser(j.user);
      }
    } catch {
      /* mantém o otimista */
    }
  }, []);

  /** Atualiza nome e/ou foto de perfil (avatar como data URL; null remove). */
  const updateProfile = useCallback(
    async (patch: { name?: string; avatarDataUrl?: string | null }): Promise<AuthUser> => {
      const r = await fetch("/api/auth/profile", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      });
      if (!r.ok) throw await readError(r);
      const j = (await r.json()) as { user: AuthUser };
      setUser(j.user);
      return j.user;
    },
    [],
  );

  /** Marca o tour de onboarding como concluído/dispensado. */
  const completeOnboarding = useCallback(async () => {
    setUser((prev) => (prev ? { ...prev, onboardingDone: true } : prev));
    try {
      const r = await postJson("/api/auth/onboarding-done", {});
      if (r.ok) {
        const j = (await r.json()) as { user: AuthUser };
        setUser(j.user);
      }
    } catch {
      /* mantém o otimista */
    }
  }, []);

  return {
    user,
    loading,
    refresh,
    login,
    verifyTwoFactor,
    register,
    logout,
    updateSettings,
    updateProfile,
    completeOnboarding,
  };
}
